import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useLocation } from "react-router-dom";
import { Box, Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { prepaymentService } from "../../../api";
import { PrepaymentConfirmationData } from "../../../api/prepaymentService";
import CustomButton from "../../../components/theming/CustomButton";
import routes from "../../../routing/routes";
import styles from "./../../../styles/custom-styles.module.scss";
import CompanyHeading from "../../../components/theming/CompanyHeading";
import Loader from "../../../components/theming/loader/Loader";

interface AfterCheckoutPageProps {
  //paymentMethod: "paypal" | "bill" | "Stripe/SEPA";
  paymentMethod: string;
}

/**
 * Page that is shown after the checkout process is completed.
 * @param paymentMethod - The payment method that was used.
 * @returns The AfterCheckoutPage component.
 */
export const AfterCheckoutPage: React.FC<AfterCheckoutPageProps> = ({
  paymentMethod,
}) => {
  const { t } = useTranslation(["booking", "common", "snackbars"]);
  const history = useHistory();
  const location = useLocation();
  const isPrepayment = paymentMethod === "prepayment";
  const [prepaymentData, setPrepaymentData] =
    useState<PrepaymentConfirmationData | null>(null);

  const messageSuccessful = isPrepayment
    ? t("afterCheckout.prepaymentSuccess", { ns: "booking" })
    : paymentMethod === "bill"
      ? t("afterCheckout.invoiceSuccess", { ns: "booking" })
      : t("afterCheckout.paymentSuccess", { ns: "booking" });
  const messageFailure = isPrepayment
    ? t("afterCheckout.prepaymentFail", { ns: "booking" })
    : paymentMethod === "bill"
      ? t("afterCheckout.invoiceSendFail", { ns: "booking" })
      : t("afterCheckout.paymentFail", { ns: "booking" });
  const locationState = location.state as
    | boolean
    | { orderSuccessful: boolean; orderId?: string }
    | undefined;
  const orderSuccessful =
    typeof locationState === "boolean" ? locationState : locationState?.orderSuccessful;
  const orderId =
    typeof locationState === "object" && locationState !== null
      ? locationState.orderId
      : undefined;

  if (typeof orderSuccessful !== "boolean") {
    return <Loader loadingText={t("waitNote", { ns: "common" })} />;
  }

  useEffect(() => {
    if (!isPrepayment || !orderId || !orderSuccessful) {
      return;
    }
    prepaymentService.getPrepaymentConfirmation(orderId).then((response) => {
      setPrepaymentData(response.data);
    });
  }, [isPrepayment, orderId, orderSuccessful]);

  const accountOwner = prepaymentData?.account_owner;
  const iban = prepaymentData?.iban;
  const bic = prepaymentData?.bic;
  const bank = prepaymentData?.bank;
  const amount = prepaymentData?.amount;
  const reference = prepaymentData?.reference;
  return (
    <>
      <CompanyHeading>{t("afterCheckout.header", { ns: "booking" })}</CompanyHeading>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          marginTop: "1.25rem",
          gap: "3rem",
        }}
      >
        {orderSuccessful ? (
          <>
            <span>{messageSuccessful}</span>
            {isPrepayment ? (
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                  maxWidth: "36rem",
                  alignSelf: "stretch",
                  textAlign: "left",
                }}
              >
                <div>
                  <strong>
                    {t("prepayment.accountOwner", { ns: "booking" })}: {accountOwner}
                    <br />
                    {t("prepayment.iban", { ns: "booking" })}: {iban}
                    <br />
                    {t("prepayment.bic", { ns: "booking" })}: {bic}
                    <br />
                    {t("prepayment.bank", { ns: "booking" })}: {bank}
                    <br />
                    <br />
                    {t("prepayment.amount", { ns: "booking" })}: {amount}
                    <br />
                    {t("prepayment.reference", { ns: "booking" })}: {reference}
                  </strong>
                </div>
              </Box>
            ) : null}
          </>
        ) : (
          <span>{messageFailure}</span>
        )}

        <Box style={{ display: "flex", gap: 10, flexDirection: "column" }}>
          {!orderSuccessful && (
            <CustomButton
              fullWidth
              id="back-to-payment"
              size="large"
              onClick={() => {
                history.push(routes.checkout);
              }}
              customColor={styles.red}
              textColor={styles.white}
              hoverColor={styles["red-dark"]}
            >
              {t("afterCheckout.backToPayment", { ns: "booking" })}
            </CustomButton>
          )}

          <Button
            fullWidth
            id="my-bookings-title"
            size="large"
            variant="contained"
            color="secondary"
            onClick={() => {
              history.push(routes.my_events);
            }}
          >
            {t("myBookings.title", { ns: "events" })}
          </Button>

          <Button
            fullWidth
            id="push-to-dashboard"
            size="large"
            variant="text"
            color="secondary"
            onClick={() => {
              history.push(routes.home);
            }}
          >
            {t("backToOverview", { ns: "common" })}
          </Button>
        </Box>
      </div>
    </>
  );
};
